<template>
  <div class="relative">
    <!-- 工具栏 -->
    <div class="absolute right-3 top-3 z-10 flex flex-col gap-1.5">
      <button class="net-btn" title="放大" @click="zoomBy(1.25)">
        <i class="fa fa-plus"></i>
      </button>
      <button class="net-btn" title="缩小" @click="zoomBy(0.8)">
        <i class="fa fa-minus"></i>
      </button>
      <button class="net-btn" title="复位视图" @click="resetView">
        <i class="fa fa-compress"></i>
      </button>
      <button class="net-btn" title="重新布局" @click="relayout">
        <i class="fa fa-refresh"></i>
      </button>
    </div>

    <!-- 图例 -->
    <div
      class="absolute left-3 bottom-3 z-10 bg-white/90 backdrop-blur rounded-lg border border-neutral-200 px-3 py-2 text-[11px] text-neutral-500 space-y-1.5 max-w-[calc(100%-6rem)]"
    >
      <div class="flex items-center gap-1.5 flex-wrap">
        <span class="font-medium text-neutral-600">重要度</span>
        <span
          v-for="lv in heatScale"
          :key="lv"
          class="inline-block rounded-full flex-shrink-0"
          :style="{
            width: 5 + lv * 0.13 + 'px',
            height: 5 + lv * 0.13 + 'px',
            background: heatColor(lv),
          }"
        ></span>
        <span>低 → 高（越大越红越重要）</span>
      </div>
      <div class="flex items-center gap-3 flex-wrap">
        <span class="flex items-center gap-1">
          <i class="fa fa-circle text-neutral-400"></i>章节
        </span>
        <span class="flex items-center gap-1">
          <i class="fa fa-circle-o text-neutral-400"></i>内置知识点
        </span>
        <span class="flex items-center gap-1">
          <i class="fa fa-minus text-neutral-300"></i>结构连线
        </span>
        <span class="flex items-center gap-1">
          <i class="fa fa-minus text-indigo-400"></i>关联连线
        </span>
        <span>{{ mode === "tree" ? "点击节点查看 · 可拖拽/缩放" : "拖拽节点 · 滚轮缩放" }}</span>
      </div>
    </div>

    <svg
      ref="svg"
      :viewBox="`0 0 ${W} ${H}`"
      class="w-full select-none bg-[radial-gradient(circle_at_50%_40%,#ffffff,#f8fafc_60%,#f1f5f9)]"
      style="height: 600px; touch-action: none"
      @mousedown="onBgDown"
      @wheel.prevent="onWheel"
      @mousemove="onMove"
      @mouseup="onUp"
      @mouseleave="onUp"
    >
      <g :transform="`translate(${tx},${ty}) scale(${scale})`">
        <!-- 连线 -->
        <path
          v-for="l in lines"
          :key="l.key"
          :d="l.d"
          fill="none"
          :stroke="l.color"
          :stroke-width="l.width"
          :stroke-dasharray="l.dash"
          :opacity="l.opacity"
        />

        <!-- 节点 -->
        <g
          v-for="n in graphNodes"
          :key="n.id"
          class="cursor-pointer"
          @mousedown.stop="onNodeDown(n, $event)"
          @click="emitSelect(n)"
          @mouseenter="hoveredId = n.id"
          @mouseleave="hoveredId = ''"
        >
          <title>{{ n.title }} · 重要度 {{ n.importance || 0 }}</title>
          <circle
            v-if="n.id === selectedId"
            :cx="n.x"
            :cy="n.y"
            :r="radius(n) + 6"
            fill="none"
            stroke="#111827"
            stroke-width="1.5"
            opacity="0.35"
          />
          <circle
            :cx="n.x"
            :cy="n.y"
            :r="radius(n)"
            :fill="heatColor(n.importance)"
            :stroke="n.isSystem && n.editable ? '#4338ca' : '#ffffff'"
            :stroke-width="n.isSystem && n.editable ? 2.5 : 2"
            :opacity="dim(n) ? 0.32 : 1"
          />
          <!-- 内置知识点：中心白点，与自建节点区分 -->
          <circle
            v-if="n.isSystem"
            :cx="n.x"
            :cy="n.y"
            :r="Math.max(2, radius(n) * 0.34)"
            fill="#ffffff"
            :opacity="dim(n) ? 0.5 : 0.9"
          />
          <text
            v-if="showLabel(n)"
            :x="labelOf(n).x"
            :y="labelOf(n).y"
            :text-anchor="labelOf(n).anchor"
            :font-size="n.type === 'chapter' ? 12 : 11"
            :font-weight="n.type === 'chapter' ? 700 : 400"
            :fill="dim(n) ? '#9ca3af' : n.type === 'chapter' ? '#111827' : '#4b5563'"
            style="paint-order: stroke; stroke: #ffffff; stroke-width: 3px"
          >
            {{ clip(n.title) }}
          </text>
        </g>
      </g>
    </svg>

    <div
      v-if="!graphNodes.length"
      class="absolute inset-0 flex flex-col items-center justify-center text-neutral-400 text-sm"
    >
      <i class="fa fa-share-alt text-3xl mb-2"></i>
      <p>{{ nodes.length ? `收到 ${nodes.length} 个知识点，但未能生成布局` : "还没有可展示的知识点" }}</p>
      <button
        v-if="nodes.length"
        class="mt-3 px-3 py-1.5 rounded-lg border border-neutral-200 text-xs text-neutral-600 hover:border-primary hover:text-primary"
        @click="relayout"
      >
        重新布局
      </button>
    </div>
  </div>
</template>

<script>
const W = 1000;
const H = 600;

export default {
  name: "KnowledgeNet",
  props: {
    nodes: { type: Array, default: () => [] },
    edges: { type: Array, default: () => [] },
    selectedId: { type: String, default: "" },
    /** net = 网状图谱；tree = 知识树 */
    mode: { type: String, default: "net" },
  },
  emits: ["select"],
  data() {
    return {
      W,
      H,
      graphNodes: [],
      tx: 0,
      ty: 0,
      scale: 1,
      hoveredId: "",
      dragNode: null,
      dragOffset: { x: 0, y: 0 },
      panning: false,
      panStart: { x: 0, y: 0, tx: 0, ty: 0 },
      moved: false,
      heatScale: [25, 45, 65, 85, 100],
    };
  },
  computed: {
    neighbors() {
      const active = this.hoveredId || this.selectedId;
      if (!active) return null;
      const set = new Set([active]);
      this.edges.forEach((e) => {
        if (e.source === active) set.add(e.target);
        if (e.target === active) set.add(e.source);
      });
      return set;
    },
    posMap() {
      const m = {};
      this.graphNodes.forEach((n) => {
        m[n.id] = n;
      });
      return m;
    },
    lines() {
      const out = [];
      const m = this.posMap;
      const active = this.hoveredId || this.selectedId;
      this.edges.forEach((e) => {
        const a = m[e.source];
        const b = m[e.target];
        if (!a || !b) return;
        const isRel = e.type === "rel";
        const touched = active && (e.source === active || e.target === active);
        let d;
        if (isRel || this.mode !== "tree") {
          d = `M${a.x},${a.y} L${b.x},${b.y}`;
        } else {
          // 树形结构用横向贝塞尔，层次更清晰
          const dx = Math.max(30, Math.abs(b.x - a.x) * 0.45);
          d = `M${a.x},${a.y} C${a.x + dx},${a.y} ${b.x - dx},${b.y} ${b.x},${b.y}`;
        }
        out.push({
          key: `${e.type}:${e.source}>${e.target}`,
          d,
          color: isRel ? "#818cf8" : this.mode === "tree" ? "#d1d5db" : "#cbd5e1",
          width: isRel ? (touched ? 2 : 1.2) : touched ? 2 : 1.2,
          dash: isRel ? "4 4" : "",
          opacity: active ? (touched ? 1 : 0.22) : isRel ? 0.85 : 0.9,
        });
      });
      return out;
    },
  },
  mounted() {
    this.relayout();
  },
  watch: {
    nodes: {
      handler() {
        this.scheduleLayout();
      },
      immediate: true,
    },
    edges() {
      this.scheduleLayout();
    },
    mode() {
      this.scheduleLayout();
    },
  },
  methods: {
    /** nodes 与 edges 往往同时更新，合并成一次布局计算 */
    scheduleLayout() {
      clearTimeout(this._layoutTimer);
      this._layoutTimer = setTimeout(() => this.relayout(), 0);
    },
    clip(title) {
      const t = title || "";
      return t.length > 12 ? t.slice(0, 12) + "…" : t;
    },

    /* ---------- 重要度 → 大小 / 颜色 ---------- */
    heatColor(importance) {
      const v = Math.max(0, Math.min(100, Number(importance) || 0)) / 100;
      const hue = 205 - 205 * v; // 蓝 → 红
      const light = 60 - 16 * v;
      return `hsl(${hue.toFixed(0)}, 76%, ${light.toFixed(0)}%)`;
    },
    radius(n) {
      const imp = Math.max(0, Math.min(100, Number(n.importance) || 0));
      return n.type === "chapter" ? 12 + imp * 0.1 : 6.5 + imp * 0.13;
    },
    labelOf(n) {
      if (this.mode === "tree") {
        return { x: n.x + this.radius(n) + 7, y: n.y + 4, anchor: "start" };
      }
      return { x: n.x, y: n.y + this.radius(n) + 13, anchor: "middle" };
    },
    dim(n) {
      const set = this.neighbors;
      if (!set) return false;
      return !set.has(n.id);
    },
    showLabel(n) {
      if (this.mode === "tree") return true;
      if (n.type === "chapter") return true;
      if ((n.wrongCount || 0) > 0) return true;
      if (this.graphNodes.length <= 40) return true;
      return n.id === this.hoveredId || n.id === this.selectedId;
    },
    emitSelect(n) {
      if (this.moved) return;
      this.$emit("select", n);
    },

    /* ---------- 布局 ---------- */
    relayout() {
      if (!(this.nodes || []).length) {
        this.graphNodes = [];
        return;
      }
      if (this.mode === "tree") {
        this.layoutTree();
      } else {
        this.layoutForce();
      }
    },

    /** 知识树：根在左、按层级向右展开，父节点纵向居中 */
    layoutTree() {
      const src = this.nodes || [];
      const byId = {};
      src.forEach((n) => {
        byId[n.id] = n;
      });

      const children = {};
      const roots = [];
      src.forEach((n) => {
        const p = n.parentId;
        if (p && byId[p]) {
          (children[p] = children[p] || []).push(n.id);
        } else {
          roots.push(n.id);
        }
      });

      const depth = {};
      const queue = roots.slice();
      roots.forEach((r) => {
        depth[r] = 0;
      });
      while (queue.length) {
        const id = queue.shift();
        (children[id] || []).forEach((k) => {
          depth[k] = (depth[id] || 0) + 1;
          queue.push(k);
        });
      }

      const SLOT = 36;
      const XGAP = 216;
      const pos = {};
      let cursor = 0;
      const visit = (id) => {
        const kids = children[id] || [];
        if (!kids.length) {
          pos[id] = { y: 40 + cursor * SLOT };
          cursor++;
        } else {
          kids.forEach(visit);
          pos[id] = {
            y: (pos[kids[0]].y + pos[kids[kids.length - 1]].y) / 2,
          };
        }
        pos[id].x = 72 + (depth[id] || 0) * XGAP;
      };
      roots.forEach(visit);
      // 环形依赖等异常数据兜底
      src.forEach((n) => {
        if (!pos[n.id]) {
          pos[n.id] = { x: 72, y: 40 + cursor * SLOT };
          cursor++;
        }
      });

      this.graphNodes = src.map((n) => ({
        ...n,
        x: Math.round(pos[n.id].x),
        y: Math.round(pos[n.id].y),
      }));
      this.fitView();
    },

    /** 网状图谱：斥力 + 弹簧 + 向心 */
    layoutForce() {
      const src = this.nodes || [];
      const pts = src.map((n, i) => {
        const angle = (i / src.length) * Math.PI * 2;
        const ring = n.type === "chapter" ? 0.28 : 0.42;
        return {
          ...n,
          x: W / 2 + Math.cos(angle) * W * ring + (Math.random() - 0.5) * 30,
          y: H / 2 + Math.sin(angle) * H * ring + (Math.random() - 0.5) * 30,
          vx: 0,
          vy: 0,
        };
      });
      const byId = {};
      pts.forEach((p) => {
        byId[p.id] = p;
      });
      const links = [];
      (this.edges || []).forEach((e) => {
        if (byId[e.source] && byId[e.target]) {
          links.push({ a: byId[e.source], b: byId[e.target], rel: e.type === "rel" });
        }
      });

      const iterations = pts.length > 240 ? 160 : 320;
      const k = 78;
      for (let it = 0; it < iterations; it++) {
        const cool = 1 - it / iterations;
        for (let i = 0; i < pts.length; i++) {
          const a = pts[i];
          for (let j = i + 1; j < pts.length; j++) {
            const b = pts[j];
            let dx = a.x - b.x;
            let dy = a.y - b.y;
            let d2 = dx * dx + dy * dy;
            if (d2 < 0.01) {
              dx = (Math.random() - 0.5) * 2;
              dy = (Math.random() - 0.5) * 2;
              d2 = 1;
            }
            const d = Math.sqrt(d2);
            const minD = a.type === "chapter" || b.type === "chapter" ? 70 : 46;
            const force = ((k * k) / d2) * (d < minD ? 2.2 : 1);
            const fx = (dx / d) * force;
            const fy = (dy / d) * force;
            a.vx += fx * 0.5;
            a.vy += fy * 0.5;
            b.vx -= fx * 0.5;
            b.vy -= fy * 0.5;
          }
        }
        links.forEach((l) => {
          const len = l.rel ? 190 : 105;
          const dx = l.b.x - l.a.x;
          const dy = l.b.y - l.a.y;
          const d = Math.sqrt(dx * dx + dy * dy) || 0.01;
          const force = (d - len) * (l.rel ? 0.02 : 0.05);
          const fx = (dx / d) * force;
          const fy = (dy / d) * force;
          l.a.vx += fx;
          l.a.vy += fy;
          l.b.vx -= fx;
          l.b.vy -= fy;
        });
        pts.forEach((p) => {
          p.vx += (W / 2 - p.x) * 0.002;
          p.vy += (H / 2 - p.y) * 0.002;
          p.vx *= 0.72 * cool + 0.28;
          p.vy *= 0.72 * cool + 0.28;
          p.x += Math.max(-22, Math.min(22, p.vx));
          p.y += Math.max(-22, Math.min(22, p.vy));
          p.vx *= 0.45;
          p.vy *= 0.45;
          p.x = Math.max(40, Math.min(W - 40, p.x));
          p.y = Math.max(34, Math.min(H - 40, p.y));
        });
      }

      this.graphNodes = pts.map((p) => ({
        id: p.id,
        title: p.title,
        subject: p.subject,
        stage: p.stage,
        parentId: p.parentId,
        isSystem: p.isSystem,
        editable: p.editable,
        type: p.type,
        wrongCount: p.wrongCount || 0,
        relCount: p.relCount || 0,
        childCount: p.childCount || 0,
        mastery: p.mastery ?? 100,
        importance: p.importance ?? 25,
        x: Math.round(p.x),
        y: Math.round(p.y),
      }));
      this.resetView();
    },

    /** 让内容完整落在视口内 */
    fitView() {
      const pts = this.graphNodes;
      if (!pts.length) return;
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;
      pts.forEach((p) => {
        const r = this.radius(p);
        minX = Math.min(minX, p.x - r);
        maxX = Math.max(maxX, p.x + r);
        minY = Math.min(minY, p.y - r);
        maxY = Math.max(maxY, p.y + r);
      });
      const pad = 60;
      const bw = Math.max(1, maxX - minX) + pad * 2;
      const bh = Math.max(1, maxY - minY) + pad * 2;
      const s = Math.min(W / bw, H / bh, 1.15);
      this.scale = s;
      this.tx = W / 2 - ((minX + maxX) / 2) * s;
      this.ty = H / 2 - ((minY + maxY) / 2) * s;
    },

    /* ---------- 交互 ---------- */
    svgPoint(evt) {
      const svg = this.$refs.svg;
      if (!svg) return { x: 0, y: 0 };
      const pt = svg.createSVGPoint();
      pt.x = evt.clientX;
      pt.y = evt.clientY;
      const p = pt.matrixTransform(svg.getScreenCTM().inverse());
      return { x: (p.x - this.tx) / this.scale, y: (p.y - this.ty) / this.scale };
    },
    onNodeDown(n, evt) {
      this.moved = false;
      const p = this.svgPoint(evt);
      this.dragNode = n;
      this.dragOffset = { x: n.x - p.x, y: n.y - p.y };
    },
    onBgDown(evt) {
      this.moved = false;
      this.panning = true;
      this.panStart = { x: evt.clientX, y: evt.clientY, tx: this.tx, ty: this.ty };
    },
    onMove(evt) {
      if (this.dragNode) {
        const p = this.svgPoint(evt);
        this.dragNode.x = p.x + this.dragOffset.x;
        this.dragNode.y = p.y + this.dragOffset.y;
        this.moved = true;
      } else if (this.panning) {
        const dx = evt.clientX - this.panStart.x;
        const dy = evt.clientY - this.panStart.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) this.moved = true;
        this.tx = this.panStart.tx + dx;
        this.ty = this.panStart.ty + dy;
      }
    },
    onUp() {
      this.dragNode = null;
      this.panning = false;
      setTimeout(() => {
        this.moved = false;
      }, 0);
    },
    onWheel(evt) {
      const svg = this.$refs.svg;
      if (!svg) return;
      const rect = svg.getBoundingClientRect();
      const mx = ((evt.clientX - rect.left) / rect.width) * W;
      const my = ((evt.clientY - rect.top) / rect.height) * H;
      const next = Math.max(0.3, Math.min(3, this.scale * (evt.deltaY < 0 ? 1.12 : 0.9)));
      const ratio = next / this.scale;
      this.tx = mx - (mx - this.tx) * ratio;
      this.ty = my - (my - this.ty) * ratio;
      this.scale = next;
    },
    zoomBy(f) {
      const next = Math.max(0.3, Math.min(3, this.scale * f));
      const ratio = next / this.scale;
      this.tx = W / 2 - (W / 2 - this.tx) * ratio;
      this.ty = H / 2 - (H / 2 - this.ty) * ratio;
      this.scale = next;
    },
    resetView() {
      if (this.mode === "tree") {
        this.fitView();
        return;
      }
      this.tx = 0;
      this.ty = 0;
      this.scale = 1;
    },
  },
};
</script>

<style scoped>
.net-btn {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  color: #6b7280;
  font-size: 12px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}
.net-btn:hover {
  color: #111827;
  border-color: #d1d5db;
}
</style>
